import { ErrorBoundary } from "@babackapp/ui/components/error-boundary";
import { ReturnRequestDto } from "@babackapp/private-api-client/types";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { Label } from "@babackapp/ui/components/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@babackapp/ui/components/table";

import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { getAllAmounts } from "@/pages/(app)/returns/view/components/financial-card/financial-card";
import { getLineItemNotReturnedQuantity } from "@babackapp/calculations/get-line-item-not-returned-quantity";
import { getLineItemNotTreatedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-not-treated-by-logistician-quantity";
import { getLineItemPaidPrice } from "@babackapp/calculations/get-line-item-paid-price";
import { getLineItemRejectedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-rejected-by-logistician-quantity";
import { getLineItemRejectedByMerchantQuantity } from "@babackapp/calculations/get-line-item-rejected-by-merchant-quantity";
import { getLineItemTreatedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-treated-by-logistician-quantity";
import { getLineItemTreatedByMerchantQuantity } from "@babackapp/calculations/get-line-item-treated-by-merchant-quantity";
import { getLineItemValidatedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-validated-by-logistician-quantity";
import { getLineItemValidatedByMerchantQuantity } from "@babackapp/calculations/get-line-item-validated-by-merchant-quantity";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { getLineItemNotTreatedByMerchantQuantity } from "@babackapp/calculations/get-line-item-not-treated-by-merchant-quantity";
type ReturnRequestDebugViewProps = {
  returnRequest: ReturnRequestDto;
};

type DebugLineProps = {
  name: string;
  value: any;
};

const debug = localStorage.getItem("debug");

function DebugLine({ name: key, value }: DebugLineProps) {
  return (
    <TableRow>
      <TableCell className="p-1 font-medium text-muted-foreground">
        {key}
      </TableCell>
      <TableCell className="p-1 font-medium">{value}</TableCell>
    </TableRow>
  );
}

function DebugHeader() {
  return (
    <TableHeader>
      <TableRow>
        <TableHead className="p-1 h-auto">Key</TableHead>
        <TableHead className="p-1 h-auto">Value</TableHead>
      </TableRow>
    </TableHeader>
  );
}

function OverallDebugView({ returnRequest }: ReturnRequestDebugViewProps) {
  const { data: shop } = useCurrentShopSuspense();
  const amounts = getAllAmounts(returnRequest, shop);

  return (
    <div className="space-y-2">
      <Label>Overall</Label>
      <div className="rounded-lg border">
        <Table>
          <DebugHeader />
          <TableBody>
            <DebugLine name="ID" value={returnRequest.id} />
            <DebugLine name="Order Id." value={returnRequest.orderId} />
            <DebugLine name="Order name" value={returnRequest.orderName} />
            {Object.entries(amounts).map(([key, value]) => (
              <DebugLine
                key={key}
                name={key}
                value={
                  typeof value === "object" && value !== null
                    ? JSON.stringify(value)
                    : value
                }
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function LineItemsDebugView({ returnRequest }: ReturnRequestDebugViewProps) {
  return (
    <div className="space-y-2">
      <Label> Line Items calculations</Label>
      <Accordion type="single">
        {returnRequest.line_items.map((lineItem) => (
          <AccordionItem
            value={lineItem.line_item_id}
            key={lineItem.line_item_id}
          >
            <AccordionTrigger className="ml-2">
              {lineItem.title}
            </AccordionTrigger>
            <AccordionContent>
              <div className="rounded-lg border">
                <Table>
                  <DebugHeader />
                  <TableBody>
                    <DebugLine name="Name" value={lineItem.title} />
                    <DebugLine name="ID" value={lineItem.line_item_id} />
                    <DebugLine name="quantity" value={lineItem.quantity} />
                    <DebugLine
                      name="quantity bought"
                      value={lineItem.quantity_bought}
                    />
                    <DebugLine
                      name="paid price"
                      value={getLineItemPaidPrice({ lineItem })}
                    />
                    <DebugLine
                      name="total returned paid price"
                      value={getLineItemPaidPrice({
                        lineItem,
                        quantity: lineItem.quantity,
                      })}
                    />
                    <DebugLine
                      name="total order paid price"
                      value={getLineItemPaidPrice({
                        lineItem,
                        quantity: lineItem.quantity_bought,
                      })}
                    />
                    <DebugLine
                      name="not returned quantity"
                      value={getLineItemNotReturnedQuantity({ lineItem })}
                    />
                    <DebugLine
                      name="not treated by merchant quantity"
                      value={getLineItemNotTreatedByMerchantQuantity({
                        lineItem,
                      })}
                    />
                    <DebugLine
                      name="not treated by logistician quantity"
                      value={getLineItemNotTreatedByLogisticianQuantity({
                        lineItem,
                      })}
                    />
                    <DebugLine
                      name="rejected by logistician quantity"
                      value={getLineItemRejectedByLogisticianQuantity({
                        lineItem,
                      })}
                    />
                    <DebugLine
                      name="rejected by merchant quantity"
                      value={getLineItemRejectedByMerchantQuantity({
                        lineItem,
                      })}
                    />
                    <DebugLine
                      name="treated by logistician quantity"
                      value={getLineItemTreatedByLogisticianQuantity({
                        lineItem,
                      })}
                    />
                    <DebugLine
                      name="treated by merchant quantity"
                      value={getLineItemTreatedByMerchantQuantity({ lineItem })}
                    />
                    <DebugLine
                      name="validated by logistician quantity"
                      value={getLineItemValidatedByLogisticianQuantity({
                        lineItem,
                      })}
                    />
                    <DebugLine
                      name="validated by merchant quantity"
                      value={getLineItemValidatedByMerchantQuantity({
                        lineItem,
                      })}
                    />
                  </TableBody>
                </Table>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export function ReturnRequestDebugView({
  returnRequest,
}: ReturnRequestDebugViewProps) {
  if (!debug) {
    return null;
  }
  return (
    <ErrorBoundary fallback={<p>⚠️Something went wrong</p>}>
      <Card className="">
        <CardHeader>
          <CardTitle>Return Request Debug View</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-8">
          <OverallDebugView returnRequest={returnRequest} />
          <LineItemsDebugView returnRequest={returnRequest} />
        </CardContent>
      </Card>
    </ErrorBoundary>
  );
}
